import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, X, SlidersHorizontal } from "lucide-react";

/**
 * Catalog filter primitives — two exports that share URL-synced state via
 * `useCatalogFilterParams()`:
 *
 *   <CatalogFilterToolbar />  — top strip (mobile drawer trigger + sort + count + active chip row)
 *   <CatalogFilterSidebar />  — left-column accordion (renders inside a parent grid column)
 *
 * Both are stateless and drive off the URL, so the app can arrange them
 * however it wants without prop-drilling filter state.
 */

const STANDARD_FACETS = {
  color: ["Black", "White", "Ivory", "Champagne", "Blush", "Pink", "Red", "Burgundy", "Coral", "Yellow", "Green", "Emerald", "Teal", "Blue", "Navy", "Royal", "Purple", "Lavender", "Silver", "Gold", "Rose Gold"],
  silhouette: ["A-Line", "Ball Gown", "Fit & Flare", "Mermaid", "Sheath", "Trumpet", "Two-Piece", "Jumpsuit"],
  neckline: ["Sweetheart", "Strapless", "Halter", "V-Neck", "Off-Shoulder", "Illusion", "Square", "High Neck", "One-Shoulder", "Plunging"],
  length: ["Floor", "Tea", "Knee", "Midi", "Short", "Mini", "High-Low"],
  sleeve: ["Sleeveless", "Short Sleeve", "Long Sleeve", "Cap Sleeve", "Puff Sleeve"],
  size: ["00", "0", "2", "4", "6", "8", "10", "12", "14", "16", "18", "20", "22", "24"],
};
const COLOR_SWATCH = {
  Black: "#0A0A0A", White: "#FAFAFA", Ivory: "#F7F1E7", Champagne: "#F7E7CE", Blush: "#F3D1D3",
  Pink: "#FFB6C1", Red: "#C8102E", Burgundy: "#5C1A22", Coral: "#FF7F50", Yellow: "#F5CB3A",
  Green: "#3F8F3A", Emerald: "#087E5B", Teal: "#0D8A8A", Blue: "#3A6BD6", Navy: "#0A2452",
  Royal: "#1E3A8A", Purple: "#7A3AA8", Lavender: "#C6B5E0", Silver: "#C5C6C7",
  Gold: "#D4A94A", "Rose Gold": "#B76E79",
};
const PRICE_RANGES = [
  { id: "under-200", label: "Under $200", min: 0, max: 200 },
  { id: "200-400",  label: "$200 — $400", min: 200, max: 400 },
  { id: "400-600",  label: "$400 — $600", min: 400, max: 600 },
  { id: "600-800",  label: "$600 — $800", min: 600, max: 800 },
  { id: "over-800", label: "Over $800", min: 800, max: 9999 },
];
const SORT_OPTIONS = [
  { value: "trending", label: "Trending" },
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price · Low to High" },
  { value: "price_desc", label: "Price · High to Low" },
];

const TUX_PIECE_TYPES = ["Men's Jacket", "Men's Pants", "Men's Vest", "Men's Shirt"];

export function useCatalogFilterParams() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => ({
    color: params.getAll("color"),
    silhouette: params.getAll("silhouette"),
    neckline: params.getAll("neckline"),
    length: params.getAll("length"),
    sleeve: params.getAll("sleeve"),
    size: params.getAll("size"),
    designer: params.getAll("designer"),
    product_type: params.getAll("product_type"),
    price_min: params.get("price_min") ? Number(params.get("price_min")) : undefined,
    price_max: params.get("price_max") ? Number(params.get("price_max")) : undefined,
    sort: params.get("sort") || "trending",
  }), [params]);

  const setFilters = (updater) => {
    const next = typeof updater === "function" ? updater(filters) : updater;
    const sp = new URLSearchParams();
    for (const [k, v] of Object.entries(next)) {
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
      if (Array.isArray(v)) v.forEach((x) => sp.append(k, x));
      else sp.set(k, String(v));
    }
    setParams(sp, { replace: true });
  };
  return [filters, setFilters];
}

function useFacetSections(designers, hideDesigner, context = "dresses", availableFacets = null) {
  return useMemo(() => {
    // Intersect the standard fashion vocabulary with what's actually in the DB so
    // students never see filters that return zero looks. Falls back to the full
    // list if the parent hasn't loaded facets yet (initial render).
    const allow = (key, options) => {
      if (!availableFacets || !availableFacets[key]) return options;
      const set = new Set(availableFacets[key].map((x) => (x.value || x).toLowerCase()));
      return options.filter((o) => set.has(String(o).toLowerCase()));
    };
    const sections = [
      { key: "color", label: "Color", options: allow("color", STANDARD_FACETS.color), swatches: true },
    ];
    if (context === "tuxedos") {
      sections.push({ key: "product_type", label: "Piece", options: allow("product_type", TUX_PIECE_TYPES) });
    }
    if (context === "dresses") {
      sections.push({ key: "silhouette", label: "Silhouette", options: allow("silhouette", STANDARD_FACETS.silhouette) });
      sections.push({ key: "neckline", label: "Neckline", options: allow("neckline", STANDARD_FACETS.neckline) });
      sections.push({ key: "length", label: "Length", options: allow("length", STANDARD_FACETS.length) });
      sections.push({ key: "sleeve", label: "Sleeve", options: allow("sleeve", STANDARD_FACETS.sleeve) });
    }
    sections.push({ key: "size", label: "Size", options: allow("size", STANDARD_FACETS.size) });
    if (!hideDesigner && designers.length > 0) {
      sections.push({
        key: "designer", label: "Designer",
        options: designers.map((d) => d.designer || d.name).filter(Boolean),
      });
    }
    // Prune sections with 0 remaining options (defensive — usually only matters for tux size etc.)
    return sections.filter((s) => (s.options || []).length > 0);
  }, [designers, hideDesigner, context, availableFacets]);
}

/** The accordion — shared between desktop sidebar and mobile drawer. */
function FacetPanel({ designers, hideDesigner, context, availableFacets }) {
  const [filters, setFilters] = useCatalogFilterParams();
  const [openSection, setOpenSection] = useState("color");
  const sections = useFacetSections(designers, hideDesigner, context, availableFacets);

  const toggleChip = (key, val) => {
    setFilters((prev) => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val] };
    });
  };
  const priceActive = filters.price_min != null || filters.price_max != null;
  const setPrice = (r) => setFilters((prev) => (
    prev.price_min === r.min && prev.price_max === r.max
      ? { ...prev, price_min: undefined, price_max: undefined }
      : { ...prev, price_min: r.min, price_max: r.max }
  ));

  return (
    <div className="space-y-1">
      {sections.map((s) => {
        const active = (filters[s.key] || []).length;
        const isOpen = openSection === s.key;
        return (
          <div key={s.key} className="border-b border-cream" data-testid={`filter-group-${s.key}`}>
            <button
              type="button"
              onClick={() => setOpenSection(isOpen ? null : s.key)}
              className="w-full flex items-center justify-between py-3 text-left font-label text-[11px] tracking-widest text-ink hover:text-rosemetal"
              data-testid={`filter-toggle-${s.key}`}
            >
              <span>
                {s.label.toUpperCase()}
                {active > 0 && <span className="ml-2 bg-rosemetal text-cream rounded-full text-[9px] px-1.5 py-0.5">{active}</span>}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }} className="overflow-hidden">
                  <div className="pb-4 flex flex-wrap gap-1.5">
                    {s.options.map((opt) => {
                      const selected = (filters[s.key] || []).includes(opt);
                      return (
                        <button
                          key={opt}
                          onClick={() => toggleChip(s.key, opt)}
                          data-testid={`filter-chip-${s.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-body text-xs border transition-colors ${
                            selected ? "border-rosemetal bg-rosemetal text-cream" : "border-cream text-ink hover:border-ink"
                          }`}
                        >
                          {s.swatches && COLOR_SWATCH[opt] && (
                            <span
                              aria-hidden
                              className="w-3 h-3 rounded-full border border-ink/20"
                              style={{ backgroundColor: COLOR_SWATCH[opt] }}
                            />
                          )}
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}

      <div className="border-b border-cream" data-testid="filter-group-price">
        <button
          type="button"
          onClick={() => setOpenSection(openSection === "price" ? null : "price")}
          className="w-full flex items-center justify-between py-3 text-left font-label text-[11px] tracking-widest text-ink hover:text-rosemetal"
        >
          <span>
            PRICE {priceActive && <span className="ml-2 bg-rosemetal text-cream rounded-full text-[9px] px-1.5 py-0.5">1</span>}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openSection === "price" ? "rotate-180" : ""}`} />
        </button>
        <AnimatePresence initial={false}>
          {openSection === "price" && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
              <div className="pb-4 flex flex-col gap-1.5">
                {PRICE_RANGES.map((r) => {
                  const active = filters.price_min === r.min && filters.price_max === r.max;
                  return (
                    <button
                      key={r.id}
                      onClick={() => setPrice(r)}
                      data-testid={`filter-price-${r.id}`}
                      className={`text-left px-3 py-1.5 rounded-sm font-body text-xs border transition-colors ${
                        active ? "border-rosemetal bg-blush/40 text-rosemetal" : "border-cream text-ink hover:border-ink"
                      }`}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/** Desktop left-column sticky sidebar. Renders NOTHING on mobile (mobile uses the drawer inside the toolbar). */
export function CatalogFilterSidebar({ designers = [], hideDesigner = false, context = "dresses", availableFacets = null }) {
  const [filters, setFilters] = useCatalogFilterParams();
  const clearAll = () => setFilters({ sort: filters.sort });
  const activeTotal = (filters.color?.length || 0) + (filters.silhouette?.length || 0)
    + (filters.neckline?.length || 0) + (filters.length?.length || 0)
    + (filters.sleeve?.length || 0) + (filters.size?.length || 0)
    + (filters.designer?.length || 0) + (filters.product_type?.length || 0)
    + ((filters.price_min != null || filters.price_max != null) ? 1 : 0);
  return (
    <aside className="hidden lg:block" data-testid="filter-sidebar">
      <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-3">
        <div className="flex items-baseline justify-between mb-3">
          <p className="font-label text-[10px] tracking-widest text-ink">FILTERS{activeTotal > 0 ? ` · ${activeTotal}` : ""}</p>
          {activeTotal > 0 && (
            <button onClick={clearAll} className="font-label text-[10px] tracking-widest text-rosemetal hover:text-ink underline underline-offset-2" data-testid="filter-clear-sidebar">
              CLEAR
            </button>
          )}
        </div>
        <FacetPanel designers={designers} hideDesigner={hideDesigner} context={context} availableFacets={availableFacets} />
      </div>
    </aside>
  );
}

/** Toolbar strip — mobile drawer trigger + active chips + sort + count. Renders on all breakpoints. */
export function CatalogFilterToolbar({ designers = [], hideDesigner = false, activeCount = 0, context = "dresses", availableFacets = null }) {
  const [filters, setFilters] = useCatalogFilterParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const sections = useFacetSections(designers, hideDesigner, context, availableFacets);

  const activeChips = sections.flatMap((s) =>
    (filters[s.key] || []).map((v) => ({ key: s.key, val: v, label: v }))
  );
  const priceActive = filters.price_min != null || filters.price_max != null;
  const activeTotal = activeChips.length + (priceActive ? 1 : 0);
  const removeChip = (key, val) => setFilters((prev) => ({ ...prev, [key]: (prev[key] || []).filter((v) => v !== val) }));
  const clearAll = () => setFilters({ sort: filters.sort });
  const setSort = (v) => setFilters((prev) => ({ ...prev, sort: v }));

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="filter-toolbar-btn"
            className="lg:hidden inline-flex items-center gap-2 border border-cream rounded-full px-4 py-2 font-label text-[10px] tracking-widest hover:border-ink"
          >
            <SlidersHorizontal className="w-3.5 h-3.5" /> FILTERS{activeTotal > 0 ? ` · ${activeTotal}` : ""}
          </button>
          {activeTotal > 0 && (
            <button
              onClick={clearAll}
              data-testid="filter-clear"
              className="hidden lg:inline font-label text-[10px] tracking-widest text-rosemetal hover:text-ink underline underline-offset-2"
            >
              CLEAR ALL ({activeTotal})
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-3">
          <span className="font-label text-[10px] tracking-widest text-ink/60">
            {activeCount.toLocaleString()} {activeCount === 1 ? "look" : "looks"}
          </span>
          <div className="relative">
            <select
              value={filters.sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="filter-sort"
              className="appearance-none bg-transparent border border-cream rounded-full px-4 py-2 pr-8 font-label text-[10px] tracking-widest hover:border-ink cursor-pointer focus:outline-none focus:border-rosemetal"
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label.toUpperCase()}</option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-ink/50" />
          </div>
        </div>
      </div>

      {(activeChips.length > 0 || priceActive) && (
        <div className="flex flex-wrap gap-1.5 mb-6" data-testid="filter-active-chips">
          {activeChips.map(({ key, val }) => (
            <button
              key={`${key}-${val}`}
              onClick={() => removeChip(key, val)}
              className="inline-flex items-center gap-1.5 bg-blush/50 border border-cream rounded-full px-3 py-1 font-body text-xs text-ink hover:bg-blush transition-colors"
            >
              {val} <X className="w-3 h-3" />
            </button>
          ))}
          {priceActive && (
            <button
              onClick={() => setFilters((p) => ({ ...p, price_min: undefined, price_max: undefined }))}
              className="inline-flex items-center gap-1.5 bg-blush/50 border border-cream rounded-full px-3 py-1 font-body text-xs text-ink hover:bg-blush transition-colors"
            >
              ${filters.price_min}–${filters.price_max} <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDrawerOpen(false)} className="fixed inset-0 z-[70] bg-ink/50 lg:hidden" />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 z-[71] w-[85%] max-w-[380px] bg-ivory p-6 pt-4 overflow-y-auto lg:hidden"
              data-testid="filter-drawer"
            >
              <div className="flex items-center justify-between pb-3 border-b border-cream mb-2">
                <span className="font-display text-xl">Filters</span>
                <button onClick={() => setDrawerOpen(false)} className="w-8 h-8 rounded-full bg-cream flex items-center justify-center">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <FacetPanel designers={designers} hideDesigner={hideDesigner} context={context} availableFacets={availableFacets} />
              <div className="mt-6 flex gap-3">
                <button onClick={clearAll} className="flex-1 border border-ink py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-ink hover:text-cream transition-colors">CLEAR ALL</button>
                <button onClick={() => setDrawerOpen(false)} className="flex-1 bg-ink text-cream py-3 font-label text-[10px] tracking-widest rounded-sm hover:bg-rosemetal transition-colors">
                  SHOW {activeCount} LOOKS
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
